import {useMutation} from '@apollo/client'
import {useFormik} from 'formik'
import {useEffect, useState} from 'react'
import {Button, Col, Form, InputGroup, Row} from 'react-bootstrap'
import * as Yup from 'yup'
import {UPDATE_PASSWORD_ADMIN} from '../../../gql/mutations/userMutations'
import {toast} from 'react-toastify'
import {parseId} from '../../../helpers'
import {IResUpdatePassAdmin} from '../../../types/membersPolicy'
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_REQUIREMENTS,
  PASSWORD_VALIDATION_MESSAGES as V,
} from '../../../helpers/passwordPolicy'
import {FormattedMessage, useIntl} from 'react-intl'
import {ONE_USER_MESSAGES as M} from '../oneUserMessages'

interface IChangePassProps {
  id: string
  refetch: () => void
}

export const ChangePassword: React.FC<IChangePassProps> = ({id, refetch}) => {
  const intl = useIntl()
  const [gqlUpdatePass, {data, loading, error}] =
    useMutation<IResUpdatePassAdmin>(UPDATE_PASSWORD_ADMIN)
  const [showPass, setShowPass] = useState(false)
  const [showConfirmPass, setShowConfirmPass] = useState(false)

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(PASSWORD_MIN_LENGTH, intl.formatMessage(V.minLength, {min: PASSWORD_MIN_LENGTH}))
        .matches(/[A-Z]/, intl.formatMessage(V.uppercase))
        .matches(/[a-z]/, intl.formatMessage(V.lowercase))
        .matches(/[0-9]/, intl.formatMessage(V.number))
        .matches(/[^A-Za-z0-9]/, intl.formatMessage(V.special))
        .required(intl.formatMessage(V.required)),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], intl.formatMessage(V.match))
        .required(intl.formatMessage(V.required)),
    }),
    onSubmit: (v, o) => {
      if (v.password && v.password !== v.confirmPassword) {
        o.setFieldError('confirmPassword', intl.formatMessage(V.match))
        return
      }
      gqlUpdatePass({
        variables: {
          nuevaClave: v.password,
          userId: parseId(id),
        },
      })
    },
  })

  useEffect(() => {
    if (error)
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {message: String(error)}
        )
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])
  useEffect(() => {
    if (!data) return
    if (data.changeUserPasswordMutation.success) {
      toast.success(intl.formatMessage(M.toastPasswordUpdated))
      refetch()
    } else if (
      data.changeUserPasswordMutation.errors &&
      data.changeUserPasswordMutation.errors.length > 0
    ) {
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {
            message:
              data.changeUserPasswordMutation.errors[0]?.message ||
              intl.formatMessage({
                id: 'common.error.somethingHappened',
                defaultMessage: 'Something happened',
              }),
          }
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  return (
    <div className='card mb-5 mb-xl-10'>
      <div
        className='card-header border-0 cursor-pointer'
        role='button'
        data-bs-toggle='collapse'
        data-bs-target='#kt_account_profile_details'
        aria-expanded='true'
        aria-controls='kt_account_profile_details'
      >
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>
            <FormattedMessage {...M.changePasswordTitle} />
          </h3>
        </div>
      </div>

      <form onSubmit={formik.handleSubmit}>
        <div className='card-body border-top p-9'>
          <div
            className='alert py-4 mb-6'
            style={{
              backgroundColor: '#EEF4FF',
              color: '#0F172A',
              border: '1px solid #93C5FD',
            }}
          >
            <div className='fw-bold mb-2'>
              <FormattedMessage {...M.passwordRequirements} />
            </div>
            <ul className='mb-0 ps-6' style={{color: '#0F172A'}}>
              {PASSWORD_REQUIREMENTS.map((rule) => (
                <li key={rule.id}>{intl.formatMessage(rule, {min: PASSWORD_MIN_LENGTH})}</li>
              ))}
            </ul>
          </div>
          <Row>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage id='admin.users.field.password' defaultMessage='Password' />
                </Form.Label>
                <InputGroup className='mb-3'>
                  <Form.Control
                    type={showPass ? 'text' : 'password'}
                    placeholder={intl.formatMessage({
                      id: 'admin.users.field.insertPassword',
                      defaultMessage: 'Insert password',
                    })}
                    aria-label={intl.formatMessage({
                      id: 'admin.users.field.insertPassword',
                      defaultMessage: 'Insert password',
                    })}
                    aria-describedby='password'
                    name='password'
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={Boolean(formik.errors.password && formik.touched.password)}
                  />
                  <Button
                    variant='outline-secondary'
                    id='button-addon2'
                    style={{border: '1px solid #dbdfe9'}}
                    onClick={() => setShowPass((p) => !p)}
                  >
                    <i
                      className={`bi bi-${showPass ? 'eye-slash' : 'eye'} fs-2`}
                      style={{color: 'inherit'}}
                    />
                  </Button>
                </InputGroup>
                {formik.errors.password && formik.touched.password && (
                  <small className='text-danger'>{formik.errors.password}</small>
                )}
              </Form.Group>
            </Col>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>
                  <FormattedMessage
                    id='admin.users.field.confirmPassword'
                    defaultMessage='Confirm Password'
                  />
                </Form.Label>
                <InputGroup className='mb-3'>
                  <Form.Control
                    type={showConfirmPass ? 'text' : 'password'}
                    placeholder={intl.formatMessage({
                      id: 'admin.users.field.confirmPasswordPlaceholder',
                      defaultMessage: 'Confirm password',
                    })}
                    aria-label={intl.formatMessage({
                      id: 'admin.users.field.confirmPasswordPlaceholder',
                      defaultMessage: 'Confirm password',
                    })}
                    aria-describedby='Confirm password'
                    name='confirmPassword'
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={Boolean(
                      formik.errors.confirmPassword && formik.touched.confirmPassword
                    )}
                  />
                  <Button
                    variant='outline-secondary'
                    id='Confirm-pass'
                    onClick={() => setShowConfirmPass((p) => !p)}
                    style={{border: '1px solid #dbdfe9'}}
                  >
                    <i
                      className={`bi bi-${showConfirmPass ? 'eye-slash' : 'eye'} fs-2`}
                      style={{color: 'inherit'}}
                    />
                  </Button>
                </InputGroup>
                {formik.errors.confirmPassword && formik.touched.confirmPassword && (
                  <small className='text-danger'>{formik.errors.confirmPassword}</small>
                )}
              </Form.Group>
            </Col>
          </Row>
        </div>
        <div className='card-footer d-flex justify-content-end py-6 px-9'>
          <button type='submit' className='btn btn-info' disabled={loading}>
            {!loading && (
              <FormattedMessage id='common.action.saveChanges' defaultMessage='Save changes' />
            )}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                <FormattedMessage {...M.pleaseWait} />
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
